/**
 * Settings > Customer portal (tracker item 45, phase 4): the master switch, how long an invoice's registration
 * code works, how many portal logins one customer may have, how much notice a reschedule request needs and
 * whether reschedule requests without a dispatch conflict are approved automatically. The fields live on the
 * company settings (PUT /company-settings, ADMIN only). The items customers can request are chosen on Settings >
 * Items ("Offer on the customer portal").
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { getErrorMessage } from '../../utils/apiError';
import { canManagePortalSettings } from '../../utils/permissions';
import { inputClass, labelClass, primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';

export interface PortalSettingsForm {
  enabled: boolean;
  codeValidMonths: string;
  maxLogins: string;
  leadHours: string;
  autoApprove: boolean;
}

export function toPortalForm(settings: types.CompanySettings): PortalSettingsForm {
  return {
    enabled: settings.portalEnabled !== false,
    codeValidMonths: String(settings.portalCodeValidMonths ?? 12),
    maxLogins: String(settings.portalMaxLoginsPerCustomer ?? 3),
    leadHours: String(settings.portalRescheduleLeadHours ?? 48),
    autoApprove: settings.portalRescheduleAutoApprove !== false,
  };
}

const whole = (text: string) => /^\d+$/.test(text.trim());

/** Field problems (empty = fine); the ranges are the backend's. */
export function portalFormProblems(form: PortalSettingsForm): Partial<Record<'codeValidMonths' | 'maxLogins' | 'leadHours', string>> {
  const problems: Partial<Record<'codeValidMonths' | 'maxLogins' | 'leadHours', string>> = {};
  if (!whole(form.codeValidMonths) || Number(form.codeValidMonths) < 1 || Number(form.codeValidMonths) > 120) problems.codeValidMonths = 'A whole number of months from 1 to 120';
  if (!whole(form.maxLogins) || Number(form.maxLogins) < 1 || Number(form.maxLogins) > 20) problems.maxLogins = 'A whole number from 1 to 20';
  if (!whole(form.leadHours) || Number(form.leadHours) > 720) problems.leadHours = 'A whole number of hours from 0 to 720';
  return problems;
}

export function CustomerPortalSettingsPage() {
  const { user } = useAuth();
  const api = useMemo<ICompanySettingsApi>(() => createAPIClient(), []);
  const canEdit = canManagePortalSettings(user);
  const [settings, setSettings] = useState<types.CompanySettings | null>(null);
  const [form, setForm] = useState<PortalSettingsForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await api.getCompanySettings();
      setSettings(loaded);
      setForm(toPortalForm(loaded));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the customer portal settings'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const problems = form ? portalFormProblems(form) : {};

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!settings || !form || !canEdit || Object.keys(problems).length > 0) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await api.updateCompanySettings({
        companyName: settings.companyName,
        portalEnabled: form.enabled,
        portalCodeValidMonths: Number(form.codeValidMonths),
        portalMaxLoginsPerCustomer: Number(form.maxLogins),
        portalRescheduleLeadHours: Number(form.leadHours),
        portalRescheduleAutoApprove: form.autoApprove,
      });
      setSettings(saved);
      setForm(toPortalForm(saved));
      setNotice(saved.portalEnabled === false ? 'Saved. The customer portal is off.' : 'Customer portal settings saved.');
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save the customer portal settings'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">Loading customer portal settings…</main>;
  if (!settings || !form) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        {error && <Toast kind="error">{error}</Toast>}
        <p className="mt-4 text-gray-700">The customer portal settings could not be loaded.</p>
        <button type="button" className="mt-2 text-blue-700 underline min-h-[44px]" onClick={() => void load()}>Try again</button>
      </main>
    );
  }

  const set = (change: Partial<PortalSettingsForm>) => setForm({ ...form, ...change });
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Customer portal</h1>
        <p className="mt-1 text-sm text-gray-600">
          The online account where customers see their invoices and jobs, pay, ask for service and ask to reschedule.
        </p>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {!canEdit && <p role="note" className="rounded bg-gray-50 p-3 text-sm text-gray-700">Only an administrator can change these settings.</p>}

      <form onSubmit={(e) => void save(e)} className="bg-white rounded-lg shadow p-5 space-y-5" aria-label="Customer portal settings" noValidate>
        <div>
          <label className="flex min-h-[44px] items-start gap-3">
            <input type="checkbox" className="mt-1 h-5 w-5" checked={form.enabled} disabled={busy || !canEdit} onChange={(e) => set({ enabled: e.target.checked })} />
            <span>
              <span className="block font-medium text-gray-900">Customer portal on</span>
              <span className="block text-sm text-gray-600">When off, customers cannot sign in (even if they are signed in now), nobody can register and invitations are refused.</span>
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-code-months" className={labelClass}>Invoice code valid for (months)</label>
            <input id="portal-code-months" inputMode="numeric" className={`${inputClass} min-h-[44px]`} value={form.codeValidMonths} disabled={busy || !canEdit}
              onChange={(e) => set({ codeValidMonths: e.target.value })} aria-invalid={!!problems.codeValidMonths} aria-describedby="portal-code-months-hint" />
            <p id="portal-code-months-hint" className={`mt-1 text-xs ${problems.codeValidMonths ? 'text-red-700' : 'text-gray-500'}`}>{problems.codeValidMonths ?? 'A code on an invoice older than this stops working for registration.'}</p>
          </div>
          <div>
            <label htmlFor="portal-max-logins" className={labelClass}>Logins per customer</label>
            <input id="portal-max-logins" inputMode="numeric" className={`${inputClass} min-h-[44px]`} value={form.maxLogins} disabled={busy || !canEdit}
              onChange={(e) => set({ maxLogins: e.target.value })} aria-invalid={!!problems.maxLogins} aria-describedby="portal-max-logins-hint" />
            <p id="portal-max-logins-hint" className={`mt-1 text-xs ${problems.maxLogins ? 'text-red-700' : 'text-gray-500'}`}>{problems.maxLogins ?? 'Further registrations for the same customer wait in Requests > Portal access requests.'}</p>
          </div>
          <div>
            <label htmlFor="portal-lead-hours" className={labelClass}>Reschedule notice (hours)</label>
            <input id="portal-lead-hours" inputMode="numeric" className={`${inputClass} min-h-[44px]`} value={form.leadHours} disabled={busy || !canEdit}
              onChange={(e) => set({ leadHours: e.target.value })} aria-invalid={!!problems.leadHours} aria-describedby="portal-lead-hours-hint" />
            <p id="portal-lead-hours-hint" className={`mt-1 text-xs ${problems.leadHours ? 'text-red-700' : 'text-gray-500'}`}>{problems.leadHours ?? 'Customers cannot ask online to move a visit that starts sooner than this.'}</p>
          </div>
        </div>

        <div>
          <label className="flex min-h-[44px] items-start gap-3">
            <input type="checkbox" className="mt-1 h-5 w-5" checked={form.autoApprove} disabled={busy || !canEdit} onChange={(e) => set({ autoApprove: e.target.checked })} />
            <span>
              <span className="block font-medium text-gray-900">Approve reschedule requests automatically</span>
              <span className="block text-sm text-gray-600">
                When the new date and time have no conflict for the assigned technician (overlap, working hours, time off), the visit moves at once and both sides are emailed.
                Anything else waits in Requests for you.
              </span>
            </span>
          </label>
        </div>

        <button type="submit" className={primaryButtonClass} disabled={busy || !canEdit || Object.keys(problems).length > 0}>{busy ? 'Saving…' : 'Save'}</button>
      </form>

      <section className="bg-white rounded-lg shadow p-5 text-sm text-gray-700 space-y-2">
        <h2 className="font-semibold text-lg text-gray-900">What customers can request</h2>
        <p>
          The request form offers the services you mark <strong>Offer on the customer portal</strong> on the <Link to="/settings/items" className="text-blue-700 underline">Items</Link> page
          (services and labor only). Customers can always describe what they need in the notes.
        </p>
      </section>
    </main>
  );
}
